import { Route, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BeheerderShell } from "@/components/BeheerderShell";
import { Navigatiekaart } from "@/components/Navigatiekaart";
import { SkeletonLine } from "@/components/ui/skeleton";
import { countLabel } from "@/lib/counts";
import {
  useKorpsenZonderCoordinaten,
  useOndersteunersZonderPunt,
} from "@/features/reistijden/useReistijden";

/**
 * A landing page rather than two stacked tables: they are consulted on different occasions, and
 * stacking them buries the one you came for (ADR-0053).
 */
export function ReistijdenPage() {
  const { t } = useTranslation(["paginas", "navigatie"]);
  const { data: korpsenZonderPunt } = useKorpsenZonderCoordinaten();
  const { data: ondersteunersZonderPunt } = useOndersteunersZonderPunt();

  return (
    <BeheerderShell
      titel={t("navigatie:paginas.reistijden.label")}
      uitleg={t("reistijden.uitleg")}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Navigatiekaart
          to="/reistijden/korpsen"
          icon={Route}
          titel={t("navigatie:paginas.reistijdenKorpsen.label")}
          omschrijving={t("reistijden.kaarten.korpsen")}
          regel={<Gat aantal={korpsenZonderPunt?.length} soort="korpsen" />}
        />
        <Navigatiekaart
          to="/reistijden/ondersteuners"
          icon={Users}
          titel={t("navigatie:paginas.reistijdenOndersteuners.label")}
          omschrijving={t("reistijden.kaarten.ondersteuners")}
          regel={
            <Gat
              aantal={ondersteunersZonderPunt?.length}
              soort="ondersteuners"
            />
          }
        />
      </div>
    </BeheerderShell>
  );
}

/**
 * How many origins cannot be routed at all, rather than a count of computed pairs: what a
 * beheerder comes for is the gap. See `Navigatiekaart` for why there is no honest denominator.
 */
function Gat({
  aantal,
  soort,
}: {
  aantal: number | undefined;
  soort: "korpsen" | "ondersteuners";
}) {
  const { t } = useTranslation("paginas");

  if (aantal === undefined) {
    return <SkeletonLine />;
  }

  if (aantal === 0) {
    return <>{t(`reistijden.tellingen.${soort}Compleet` as const)}</>;
  }

  return (
    <>
      {countLabel(aantal, (count) =>
        t(`reistijden.tellingen.${soort}ZonderPunt` as const, { count }),
      )}
    </>
  );
}
